import React, { useEffect, useState } from "react";
import { Alert, Modal } from "antd";
import { useGenerateTemporaryPin } from "./hooks/useGenerateTemporaryPin";
import { ITemporaryPinModalProps } from "./interfaces/temporaryPin";
import SendTemporaryPin from "./components/SendTemporaryPin";
import ConfirmBeforeSending from "./components/ConfirmBeforeSending";
import { errorHandler } from "../../../helpers/errorHandler";

import styles from "./TemporaryPinModal.module.scss";

const TemporaryPinModal: React.FC<ITemporaryPinModalProps> = ({
  visible,
  onClose,
  ssoUserId,
  userId,
  customerId,
  customerEmail,
  customerName,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [sendEmail, setSendEmail] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { mutate, result } = useGenerateTemporaryPin();
  const isSubmitting = result.status === "loading";

  useEffect(() => {
    if (visible) {
      setCurrentStep(1);
      setSendEmail(false);
      setErrorMessage(null);
      setSuccessMessage(null);
    }
  }, [visible]);

  const handleClose = () => {
    setCurrentStep(1);
    setSendEmail(false);
    setErrorMessage(null);
    setSuccessMessage(null);
    onClose();
  };

  const handleConfirmAndSend = () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    const payload = {
      userId,
      sendEmail,
    };

    mutate(payload, {
      onSuccess: (data) => {
        setSuccessMessage(
          data?.message || "Temporary PIN generated and sent successfully.",
        );
      },
      onError: (error) => {
        setErrorMessage(errorHandler(error));
      },
    });
  };

  const resetsUsed = 0;
  const maxResets = 3;

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <SendTemporaryPin
            customerEmail={customerEmail}
            sendEmail={sendEmail}
            onSendEmailChange={setSendEmail}
            onSendPin={() => setCurrentStep(2)}
            onCancel={handleClose}
            onBack={handleClose}
          />
        );
      case 2:
        return (
          <ConfirmBeforeSending
            customerName={customerName}
            sendEmail={sendEmail}
            resetsUsed={resetsUsed}
            maxResets={maxResets}
            isSubmitting={isSubmitting}
            onConfirm={handleConfirmAndSend}
            onCancel={handleClose}
            onBack={() => setCurrentStep(1)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={640}
      zIndex={11000}
      getContainer={document.body}
      destroyOnClose
      closable={true}
      maskClosable={!isSubmitting}
    >
      {errorMessage && (
        <div style={{ marginBottom: 16, marginTop: 24 }}>
          <Alert
            type={errorMessage ? "error" : "success"}
            showIcon
            message={"Temporary PIN could not be generated"}
            description={errorMessage || undefined}
          />
        </div>
      )}
      {!successMessage ? (
        <div className={styles.modalContent}>{renderStep()}</div>
      ) : (
        <div className={styles.successState}>
          <div className={styles.successIconWrap}>
            <i className="fas fa-check-circle" />
          </div>
          <h2 className={styles.title}>Temporary PIN sent</h2>
          <p className={styles.subtitle}>{successMessage}</p>
          <div className={styles.actionButtons}>
            <button className={styles.primaryButton} onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TemporaryPinModal;
